import { z } from 'zod';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Formato HH:mm');

export const SegmentSchema = z.object({
  start: hhmm,
  /** Si end <= start, el tramo termina al día siguiente (turno nocturno). */
  end: hhmm,
});
export type Segment = z.infer<typeof SegmentSchema>;

export const BreakDefSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(80),
  /** Hora prevista de inicio. Opcional para pausas flexibles. */
  start: hhmm.nullable().default(null),
  end: hhmm.nullable().default(null),
  durationMinutes: z.number().int().min(1).max(480),
  /** Si la pausa computa como tiempo de trabajo (p. ej. "pausa del bocadillo" retribuida por convenio). */
  computable: z.boolean().default(false),
  mandatory: z.boolean().default(false),
  /** Si el trabajador debe fichar inicio y fin de esta pausa. */
  requiresClock: z.boolean().default(true),
  gps: z.enum(['INHERIT', 'REQUIRED', 'OPTIONAL', 'DISABLED']).default('INHERIT'),
  toleranceMinutes: z.number().int().min(0).max(120).default(5),
  maxMinutes: z.number().int().min(1).max(600).nullable().default(null),
  flexible: z.boolean().default(false),
  reminderBeforeMinutes: z.number().int().min(0).max(120).nullable().default(null),
  overrunAction: z.enum(['NONE', 'NOTIFY', 'INCIDENT']).default('NOTIFY'),
});
export type BreakDef = z.infer<typeof BreakDefSchema>;
export type BreakDefInput = z.input<typeof BreakDefSchema>;

export const DayTemplateSchema = z.object({
  segments: z.array(SegmentSchema).min(1).max(4),
  breaks: z.array(BreakDefSchema).max(6).default([]),
});
export type DayTemplate = z.infer<typeof DayTemplateSchema>;

export const FlexDayTemplateSchema = z.object({
  targetMinutes: z.number().int().min(1).max(1440),
  earliestStart: hhmm,
  latestEnd: hhmm,
  coreStart: hhmm.nullable().default(null),
  coreEnd: hhmm.nullable().default(null),
  breaks: z.array(BreakDefSchema).max(6).default([]),
});
export type FlexDayTemplate = z.infer<typeof FlexDayTemplateSchema>;

function weekObject<T extends z.ZodTypeAny>(day: T) {
  const d = () => day.nullable().default(null);
  return z.object({ '1': d(), '2': d(), '3': d(), '4': d(), '5': d(), '6': d(), '7': d() });
}

export const ScheduleDefinitionSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('WEEKLY'),
    /** Clave = día ISO (1 lunes … 7 domingo). null = descanso. */
    days: weekObject(DayTemplateSchema),
  }),
  z.object({
    kind: z.literal('FLEXIBLE'),
    days: weekObject(FlexDayTemplateSchema),
  }),
  z.object({
    kind: z.literal('CYCLE'),
    /** Fecha en la que comienza el día 0 del ciclo. */
    anchorDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Patrón rotativo, p. ej. 4 días de trabajo + 2 de descanso. */
    cycle: z.array(DayTemplateSchema.nullable()).min(1).max(56),
  }),
]);
export type ScheduleDefinition = z.infer<typeof ScheduleDefinitionSchema>;
export type ScheduleDefinitionInput = z.input<typeof ScheduleDefinitionSchema>;

export const ShiftTypeDefinitionSchema = DayTemplateSchema;

/** Patrón de turnos rotativo: cada posición es un id de tipo de turno o null (descanso). */
export const ShiftPatternSchema = z.object({
  anchorDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  cycle: z.array(z.string().nullable()).min(1).max(56),
});
export type ShiftPattern = z.infer<typeof ShiftPatternSchema>;

export type DayType = 'WORKDAY' | 'REST' | 'HOLIDAY' | 'CLOSURE' | 'VACATION' | 'ABSENCE' | 'UNSCHEDULED';

export interface PlannedSegment {
  start: string; // ISO UTC
  end: string; // ISO UTC
  startLocal: string; // HH:mm
  endLocal: string;
  crossesMidnight: boolean;
}

export interface PlannedBreak extends BreakDef {
  /** Inicio/fin absolutos (UTC ISO) si la pausa tiene hora prevista. */
  startAt: string | null;
  endAt: string | null;
}

export interface PlannedDay {
  date: string;
  timezone: string;
  dayType: DayType;
  kind: 'FIXED' | 'FLEXIBLE' | 'NONE';
  segments: PlannedSegment[];
  breaks: PlannedBreak[];
  /** Minutos de trabajo previstos (tramos − pausas no computables). */
  plannedMinutes: number;
  flexible: null | {
    targetMinutes: number;
    earliestStart: string;
    latestEnd: string;
    coreStart: string | null;
    coreEnd: string | null;
  };
  source: {
    type: 'OVERRIDE' | 'SHIFT' | 'SCHEDULE' | 'NONE';
    refId: string | null;
    versionId: string | null;
    shiftTypeId: string | null;
  };
  label: string | null; // nombre del festivo, tipo de ausencia, turno...
  absence: null | { typeCode: string; paid: boolean; partial: boolean; minutes: number | null };
  centerId: string | null;
}
